/**
 * What a customer is allowed to attach, and how we decide.
 *
 * The declared content type is whatever the browser felt like sending, and on an endpoint
 * that stores files and hands URLs to a WhatsApp client it is not evidence of anything. The
 * bytes are inspected instead: an allowlist of formats WhatsApp can actually deliver, matched
 * on their own signatures, so an executable renamed to .jpg is refused rather than stored.
 */

export type MediaKind = 'image' | 'video' | 'audio' | 'document'

/**
 * What a stored message's `media_type` is allowed to say.
 *
 * Deliberately a wider list than `MediaKind` rather than a widening of it. `MediaKind` is the
 * *upload* vocabulary — what a customer may attach and what `sniffMedia` can prove from the
 * bytes — and `broadcasts.media_kind` is typed on it. WhatsApp delivers three things inbound
 * that we never upload and could never sniff: a sticker, a shared contact card, and a dropped
 * pin. Folding them into `MediaKind` would quietly grant them a size ceiling and a place in
 * the upload validator they have no business having.
 */
export const MESSAGE_MEDIA_TYPES = [
  'image',
  'video',
  'audio',
  'document',
  'sticker',
  'location',
  'contact',
] as const

export type MessageMediaType = (typeof MESSAGE_MEDIA_TYPES)[number]

/**
 * The session host is a separate package and posts `mediaType` as a plain string into a typed
 * column. Checked at the door rather than trusted, so a host running older or newer code
 * cannot write a value the inbox has no way to render.
 */
export function isMessageMediaType(value: unknown): value is MessageMediaType {
  return typeof value === 'string' && (MESSAGE_MEDIA_TYPES as readonly string[]).includes(value)
}

/**
 * The body a dropped pin reads as, since a location message carries no text of its own.
 *
 * A maps link rather than a structured `location` column, deliberately: keyword rules,
 * auto-reply, the CSV export and the conversation preview all read `body`, and none of them
 * would read a new column. The trade is that coordinates end up as text and cannot be queried
 * as numbers — worth naming here rather than discovering it the first time someone asks for
 * "every job within 5 km".
 *
 * Mirrored in `session-host/src/normalize.ts` for inbound pins; the host is a separate
 * package and cannot import this. Both sides must produce the same string, or the same place
 * would read differently depending on which end dropped it.
 */
export function locationBody(location: {
  latitude: number
  longitude: number
  name?: string
  address?: string
}): string {
  const label = (location.name ?? location.address ?? '').trim()
  const link = `https://www.google.com/maps?q=${location.latitude},${location.longitude}`
  return label ? `${label} — ${link}` : link
}

export interface MediaFormat {
  kind: MediaKind
  mime: string
  extension: string
}

/**
 * Size ceilings, in bytes.
 *
 * WhatsApp's own limits, except for documents: it permits 100 MB, but a recipient on mobile
 * data will not thank anyone for a broadcast that size, and neither will the customer's
 * storage bill. Sixteen is generous for the invoices and menus people actually send.
 */
export const MAX_SIZE: Record<MediaKind, number> = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
  document: 16 * 1024 * 1024,
}

/** Bytes to read before a decision can be made; every signature below fits inside this. */
export const SNIFF_BYTES = 32

interface Signature {
  format: MediaFormat
  /** Byte values to match; null is a wildcard for lengths or ids that vary. */
  magic: (number | null)[]
  offset?: number
}

const ascii = (text: string) => [...text].map((c) => c.charCodeAt(0))

const SIGNATURES: Signature[] = [
  { format: { kind: 'image', mime: 'image/jpeg', extension: 'jpg' }, magic: [0xff, 0xd8, 0xff] },
  {
    format: { kind: 'image', mime: 'image/png', extension: 'png' },
    magic: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  },
  { format: { kind: 'image', mime: 'image/gif', extension: 'gif' }, magic: ascii('GIF8') },
  // RIFF containers name their payload at offset 8, so the header alone is ambiguous.
  {
    format: { kind: 'image', mime: 'image/webp', extension: 'webp' },
    magic: [...ascii('RIFF'), null, null, null, null, ...ascii('WEBP')],
  },
  {
    format: { kind: 'audio', mime: 'audio/wav', extension: 'wav' },
    magic: [...ascii('RIFF'), null, null, null, null, ...ascii('WAVE')],
  },
  // ISO base media: the box length precedes the 'ftyp' type.
  {
    format: { kind: 'video', mime: 'video/mp4', extension: 'mp4' },
    magic: ascii('ftyp'),
    offset: 4,
  },
  { format: { kind: 'audio', mime: 'audio/ogg', extension: 'ogg' }, magic: ascii('OggS') },
  { format: { kind: 'audio', mime: 'audio/mpeg', extension: 'mp3' }, magic: ascii('ID3') },
  { format: { kind: 'audio', mime: 'audio/mpeg', extension: 'mp3' }, magic: [0xff, 0xfb] },
  { format: { kind: 'document', mime: 'application/pdf', extension: 'pdf' }, magic: ascii('%PDF') },
  // Word, Excel and PowerPoint are all zip archives; so is a plain .zip, which is why the
  // filename decides which of them this is once the container is confirmed.
  { format: { kind: 'document', mime: 'application/zip', extension: 'zip' }, magic: [0x50, 0x4b, 0x03, 0x04] },
]

/** Office formats, resolved by extension once the zip container has been verified. */
const OFFICE: Record<string, MediaFormat> = {
  docx: {
    kind: 'document',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extension: 'docx',
  },
  xlsx: {
    kind: 'document',
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
  },
  pptx: {
    kind: 'document',
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: 'pptx',
  },
}

function matches(bytes: Uint8Array, signature: Signature): boolean {
  const offset = signature.offset ?? 0
  if (bytes.length < offset + signature.magic.length) return false
  return signature.magic.every((byte, i) => byte === null || bytes[offset + i] === byte)
}

/**
 * Identifies a file from its opening bytes.
 *
 * Returns null for anything not on the list, which is the safe answer: an unrecognised file
 * is either something WhatsApp cannot deliver or something we should not be hosting.
 */
export function sniffMedia(bytes: Uint8Array, fileName = ''): MediaFormat | null {
  for (const signature of SIGNATURES) {
    if (!matches(bytes, signature)) continue

    if (signature.format.extension === 'zip') {
      const extension = fileName.split('.').pop()?.toLowerCase() ?? ''
      return OFFICE[extension] ?? signature.format
    }
    return signature.format
  }
  return null
}

export interface MediaCheck {
  format: MediaFormat | null
  error: string | null
}

/** Sniffs and size-checks in one step, with a message fit to show the customer. */
export function checkMedia(bytes: Uint8Array, fileName: string, totalSize: number): MediaCheck {
  const format = sniffMedia(bytes, fileName)

  if (!format) {
    return {
      format: null,
      error:
        'That file type cannot be sent on WhatsApp. Images, video, audio, PDFs and Office documents are supported.',
    }
  }

  const limit = MAX_SIZE[format.kind]
  if (totalSize > limit) {
    return {
      format: null,
      error: `${format.kind === 'image' ? 'Images' : `${format.kind[0].toUpperCase()}${format.kind.slice(1)} files`} must be under ${formatBytes(limit)}. This one is ${formatBytes(totalSize)}.`,
    }
  }

  return { format, error: null }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Strips a filename down to something safe to store and to put in a Content-Disposition
 * header. Path separators, control characters and quotes are all removed rather than
 * escaped, because none of them belong in a name a recipient will see.
 */
export function safeFileName(raw: string): string {
  const base = raw.split(/[/\\]/).pop() ?? ''
  const cleaned = base
    // Control characters, the Windows-reserved set, and quotes — the last of which would
    // otherwise let a crafted name break out of the Content-Disposition header it ends up in.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f<>:"|?*\\/]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    // A leading dot hides the file; a trailing one confuses Windows.
    .replace(/^\.+/, '')
    .replace(/\.+$/, '')
  return cleaned.slice(0, 120) || 'attachment'
}
